import useSWR from "swr";
import {
  getHomepageApprovedItems,
  getHomepageRecentlySoldItems,
} from "@/lib/itemsAPI";
import { getRecentItemRequests } from "@/lib/itemRequestsAPI";
import { Item } from "@/lib/types";
import { ItemRequest } from "@/lib/types";
import { useSystemSettings } from "@/contexts/SystemSettingsContext";
import { useLocation } from "@/contexts/LocationContext";
import { getHomepageApprovedPoolLimit } from "@/lib/items/homepageDiscoveryGranularity";
import { getUserStateAndLGA } from "@/lib/locationHierarchy";

export interface HomepageData {
  approvedItems: Item[];
  recentlySoldItems: Item[];
  recentRequests: ItemRequest[];
}

const HOMEPAGE_RECENTLY_SOLD_LIMIT = 20;
const HOMEPAGE_DEDUPING_INTERVAL_MS = 5 * 60 * 1000;

const fetchHomepageData = async (
  poolLimit: number,
  userState: string | null,
  fetchRecentRequests: boolean,
): Promise<HomepageData> => {
  const [approvedItems, recentlySoldItems] = await Promise.all([
    getHomepageApprovedItems(poolLimit, userState),
    getHomepageRecentlySoldItems(HOMEPAGE_RECENTLY_SOLD_LIMIT),
  ]);

  // H4 — admin `recent_item_requests_enabled` owns fetch + visibility (no env dual gate).
  const recentRequests = fetchRecentRequests
    ? await getRecentItemRequests(50)
    : [];

  return {
    approvedItems,
    recentlySoldItems,
    recentRequests,
  };
};

export function useHomepageData() {
  const {
    homepageDiscoveryGranularity,
    homepageDiscoveryAutoWidenEnabled,
    recentItemRequestsEnabled,
    isLoading: settingsLoading,
  } = useSystemSettings();
  const { userLocation } = useLocation();
  // When auto-widen is on, the shelf can widen its reach up to the whole state,
  // so fetch the state-sized pool to give the client enough items to widen into
  // (otherwise widening re-tiers the same too-small newest-N window).
  const poolLimit = getHomepageApprovedPoolLimit(
    homepageDiscoveryAutoWidenEnabled ? "state" : homepageDiscoveryGranularity,
  );

  // Only bias the fetch to a state when the buyer actually has a location; without
  // one, keep the flat global newest-N (getUserStateAndLGA defaults to Lagos, which
  // we must NOT apply to locationless buyers). Drives a state-prioritized pool (R5b).
  const userState = userLocation?.areaName?.trim()
    ? getUserStateAndLGA({
        areaName: userLocation.areaName ?? "",
        formattedAddress: userLocation.formattedAddress,
      }).state
    : null;

  const { data, error, isLoading, isValidating } = useSWR<HomepageData>(
    settingsLoading
      ? null
      : ["homepage-data", poolLimit, userState, recentItemRequestsEnabled],
    () => fetchHomepageData(poolLimit, userState, recentItemRequestsEnabled),
    {
      revalidateOnFocus: false,
      dedupingInterval: HOMEPAGE_DEDUPING_INTERVAL_MS,
      shouldRetryOnError: true,
      errorRetryCount: 3,
    },
  );

  return {
    data: data || {
      approvedItems: [],
      recentlySoldItems: [],
      recentRequests: [],
    },
    error,
    isLoading: settingsLoading || isLoading,
    isValidating,
  };
}
